import { useEffect, useRef, useState } from 'react';

/**
 * Hook to detect when an element is visible in the viewport
 *
 * @param {React.RefObject} elementRef - Ref of the element to observe
 * @param {Object} options - Intersection observer options
 * @param {number} options.threshold - Threshold for visibility (0-1)
 * @param {string} options.rootMargin - Margin around root element
 * @returns {boolean} Whether the element is visible
 */
const useIntersectionObserver = (
  elementRef,
  { threshold = 0.1, rootMargin = '0px' } = {}
) => {
  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef(null);

  useEffect(() => {
    // Skip if no ref or not supported
    if (!elementRef?.current || typeof IntersectionObserver !== 'function') {
      return;
    }

    // Cleanup previous observer if it exists
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create observer
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold, rootMargin }
    );

    // Start observing
    observerRef.current.observe(elementRef.current);

    // Cleanup on unmount
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [elementRef, threshold, rootMargin]);

  return isVisible;
};

export default useIntersectionObserver;
